/* =============================================================================
   mbi-pdp.js — MBI PDP top section behaviour (sections/mbi-main-product.liquid)
   Version: 2026-10-07.10
   No jQuery / slick. The cover is a native CSS scroll-snap slider (swipe works
   with no JS at all); this file only adds:
     - thumbnail buttons → scroll the cover to that image
     - prev / next arrows (pointer devices) + Left/Right keys on the slider
     - active thumbnail + arrow state follow the cover (also after swipes)
     - "Read more" → smooth scroll to the full description, offset for the
       sticky header, focus moved there for keyboard / screen-reader users
     - free-delivery nudge: refreshes from /cart.js when the basket count changes
     - delivery estimate: next-working-day + standard dates in UK time,
       skipping weekends, UK bank holidays (rules + GOV.UK feed) and extra closures set on the block
   The lightbox (section-main-product.js) still binds to the slides' <figure>s.
   ========================================================================== */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initGallery(media) {
    if (media.dataset.mbiPdpReady) return;
    media.dataset.mbiPdpReady = 'true';

    const slider = media.querySelector('.mbi-pdp__slider');
    if (!slider) return;
    const slides = Array.from(slider.querySelectorAll('.mbi-pdp__slide'));
    const thumbs = Array.from(media.querySelectorAll('.mbi-pdp__thumb'));
    const thumbStrip = media.querySelector('.mbi-pdp__thumbs');
    const prev = media.querySelector('.mbi-pdp__nav--prev');
    const next = media.querySelector('.mbi-pdp__nav--next');
    if (slides.length < 2) return;

    let current = 0;

    function goTo(index) {
      const target = Math.max(0, Math.min(slides.length - 1, index));
      slider.scrollTo({
        left: slides[target].offsetLeft,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
      setActive(target);
    }

    function setActive(index) {
      if (index === current && thumbs[index] && thumbs[index].classList.contains('is-active')) return;
      current = index;
      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;

      // Keep the active thumbnail visible in its own strip (never scroll the page).
      const active = thumbs[index];
      if (thumbStrip && active) {
        const item = active.parentElement;
        const left = item.offsetLeft;
        const right = left + item.offsetWidth;
        if (left < thumbStrip.scrollLeft) {
          thumbStrip.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
        } else if (right > thumbStrip.scrollLeft + thumbStrip.clientWidth) {
          thumbStrip.scrollTo({ left: right - thumbStrip.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    }

    // Track swipes / snaps: the slide that is mostly in view is "current".
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            setActive(slides.indexOf(entry.target));
          }
        });
      },
      { root: slider, threshold: [0.6] }
    );
    slides.forEach((slide) => observer.observe(slide));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => goTo(Number(thumb.dataset.index) || 0));
    });
    if (prev) prev.addEventListener('click', () => goTo(current - 1));
    if (next) next.addEventListener('click', () => goTo(current + 1));

    slider.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(current - 1);
      }
    });

    // Re-align after layout changes (rotation / resize) so a half slide never shows.
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        slider.scrollTo({ left: slides[current].offsetLeft, behavior: 'auto' });
      }, 150);
    });
  }

  function headerOffset() {
    const nav = document.getElementById('nav-wrapper') || document.querySelector('.header');
    const rect = nav ? nav.getBoundingClientRect() : null;
    // Only offset when the header is actually pinned at the top.
    return rect && rect.top <= 0 && rect.bottom > 0 ? rect.height + 16 : 16;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('[data-mbi-pdp-readmore]');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return; // no description section on this template: normal anchor
    event.preventDefault();
    const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  function init(root) {
    (root || document).querySelectorAll('.mbi-pdp__media').forEach(initGallery);
  }

  /* ---------- "About this book" accordion ------------------------------- */
  // The header button (data-mbi-pdp-toggle) sits above the content and never
  // moves. The inline "more" opens; "Show less" closes and brings the header
  // back into view if it has scrolled off. Excerpt ↔ full description +
  // Details are swapped (both already in the HTML).
  function setAboutOpen(wrap, open, fromLess) {
    const panel = wrap.querySelector('.mbi-pdp__more');
    if (!panel) return;
    const excerpt = wrap.querySelector('[data-mbi-pdp-excerpt]');
    panel.hidden = !open;
    if (excerpt) excerpt.hidden = open;
    wrap.classList.toggle('is-open', open);
    wrap.querySelectorAll('[data-mbi-pdp-toggle], [data-mbi-pdp-more]').forEach((btn) => {
      btn.setAttribute('aria-expanded', String(open));
    });
    const header = wrap.querySelector('[data-mbi-pdp-toggle]');
    if (!open && fromLess && header) {
      const top = header.getBoundingClientRect().top;
      if (top < headerOffset()) {
        window.scrollBy({ top: top - headerOffset(), behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      header.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest) return;
    const toggle = event.target.closest('[data-mbi-pdp-toggle]');
    const more = event.target.closest('[data-mbi-pdp-more]');
    const less = event.target.closest('[data-mbi-pdp-less]');
    const trigger = toggle || more || less;
    if (!trigger) return;
    const wrap = trigger.closest('[data-mbi-pdp-expand]');
    if (!wrap) return;
    event.preventDefault();
    if (toggle) {
      setAboutOpen(wrap, toggle.getAttribute('aria-expanded') !== 'true', false);
    } else if (more) {
      setAboutOpen(wrap, true, false);
      const header = wrap.querySelector('[data-mbi-pdp-toggle]');
      if (header) header.focus({ preventScroll: true });
    } else {
      setAboutOpen(wrap, false, true);
    }
  });

  /* ---------- Free-delivery nudge --------------------------------------- */
  function formatMoney(cents, currency, locale) {
    try {
      const value = cents / 100;
      return new Intl.NumberFormat(locale || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (_error) {
      return (cents / 100).toFixed(2);
    }
  }

  function renderNudge(nudge, total) {
    // Drawer-style strip: two prerendered lines (locale text from Liquid);
    // swap them and update the amount only.
    const threshold = Number(nudge.dataset.threshold) || 0;
    if (!threshold) return;
    const remaining = threshold - total;
    const left = nudge.querySelector('.free-shipping-left');
    const qualified = nudge.querySelector('.free-shipping-qualified');
    const amount = nudge.querySelector('.mbi-pdp__free-shipping-amount');
    if (left) left.hidden = remaining <= 0;
    if (qualified) qualified.hidden = remaining > 0;
    if (amount && remaining > 0) {
      amount.textContent = formatMoney(remaining, nudge.dataset.currency, nudge.dataset.locale);
    }
    const bar = nudge.querySelector('.mbi-pdp__free-shipping-bar');
    if (bar) {
      bar.classList.toggle('is-complete', remaining <= 0);
      const fill = bar.querySelector('span');
      if (fill) fill.style.width = `${Math.max(0, Math.min(100, Math.round((total / threshold) * 100)))}%`;
    }
  }

  function initNudges() {
    const nudges = document.querySelectorAll('[data-mbi-nudge]');
    if (!nudges.length) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let timer = null;
    const refresh = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((cart) => {
            if (cart) nudges.forEach((nudge) => renderNudge(nudge, cart.total_price || 0));
          })
          .catch(() => {});
      }, 300);
    };
    // The header count is updated by the theme / Boost cart watcher after every
    // add / change, so watching it covers PDP, sticky bar, drawer and Quick View.
    document.querySelectorAll('.cart-count').forEach((count) => {
      new MutationObserver(refresh).observe(count, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ---------- Delivery estimate (UK time) -------------------------------- */
  function ukParts(date) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
    }).formatToParts(date);
    const get = (type) => (parts.find((p) => p.type === type) || {}).value;
    return {
      y: Number(get('year')), m: Number(get('month')), d: Number(get('day')),
      h: Number(get('hour')) % 24, min: Number(get('minute')),
    };
  }

  /* ---------- UK (England & Wales) bank holidays — no maintenance ------- */
  // Two layers (rev11, 07/10 — user: "I will forget to update this"):
  //  1. RULES: every standard E&W bank holiday computed for any year, forever
  //     (Easter via the Gregorian computus; weekend substitutes as GOV.UK).
  //  2. GOV.UK FEED (www.gov.uk/bank-holidays.json, CORS-open, ~2 yrs ahead):
  //     for years it covers it REPLACES the rules, so one-offs (coronation,
  //     jubilee, moved May holidays) are exact. Cached 7 days in localStorage;
  //     fetched after first render, so a slow/blocked feed never delays the
  //     dates — the rules are already correct for normal years.
  // Block setting "Extra closure dates" adds warehouse-only closures on top.
  const iso = (dt) => `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
  const utc = (y, m, d) => new Date(Date.UTC(y, m - 1, d));
  const BH_CACHE = 'mbiBankHolidays.v1';
  const BH_TTL = 7 * 24 * 60 * 60 * 1000;
  const ruleYears = new Map(); // year → Set of ISO dates (rules)
  let feedYears = new Map();   // year → Set of ISO dates (GOV.UK)

  function easterSunday(y) {
    // Anonymous Gregorian algorithm (Meeus/Jones/Butcher).
    const a = y % 19, b = Math.floor(y / 100), c = y % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    return utc(y, month, ((h + l - 7 * m + 114) % 31) + 1);
  }

  function rulesFor(y) {
    if (ruleYears.has(y)) return ruleYears.get(y);
    const out = new Set();
    const shift = (dt, days) => { const x = new Date(dt.getTime()); x.setUTCDate(x.getUTCDate() + days); return x; };
    const firstMonday = (m) => { const dt = utc(y, m, 1); return shift(dt, (8 - dt.getUTCDay()) % 7); };
    const lastMonday = (m) => { const dt = utc(y, m + 1, 0); return shift(dt, -((dt.getUTCDay() + 6) % 7)); };
    // New Year's Day → next Monday if it falls at the weekend.
    const ny = utc(y, 1, 1);
    out.add(iso(shift(ny, ny.getUTCDay() === 6 ? 2 : ny.getUTCDay() === 0 ? 1 : 0)));
    const easter = easterSunday(y);
    out.add(iso(shift(easter, -2))); // Good Friday
    out.add(iso(shift(easter, 1)));  // Easter Monday
    out.add(iso(firstMonday(5)));    // Early May
    out.add(iso(lastMonday(5)));     // Spring
    out.add(iso(lastMonday(8)));     // Summer
    // Christmas / Boxing Day with weekend substitutes (27th/28th).
    const xmasDow = utc(y, 12, 25).getUTCDay();
    const boxDow = utc(y, 12, 26).getUTCDay();
    out.add(iso(utc(y, 12, (xmasDow === 6 || xmasDow === 0) ? 27 : 25)));
    out.add(iso(utc(y, 12, (boxDow === 6 || boxDow === 0) ? 28 : 26)));
    ruleYears.set(y, out);
    return out;
  }

  function setFeed(dates) {
    const map = new Map();
    dates.forEach((d) => {
      const y = Number(d.slice(0, 4));
      if (!map.has(y)) map.set(y, new Set());
      map.get(y).add(d);
    });
    feedYears = map;
  }

  function isBankHoliday(dt) {
    const y = dt.getUTCFullYear();
    return (feedYears.get(y) || rulesFor(y)).has(iso(dt));
  }

  // Cached feed → available synchronously for the first render.
  try {
    const cached = JSON.parse(localStorage.getItem(BH_CACHE) || 'null');
    if (cached && Array.isArray(cached.dates)) setFeed(cached.dates);
  } catch (e) { /* storage blocked: rules only */ }

  function refreshBankHolidays(onUpdate) {
    try {
      const cached = JSON.parse(localStorage.getItem(BH_CACHE) || 'null');
      if (cached && Date.now() - cached.t < BH_TTL) return;
    } catch (e) { /* fall through and fetch */ }
    fetch('https://www.gov.uk/bank-holidays.json', { credentials: 'omit', mode: 'cors' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((json) => {
        const dates = ((json['england-and-wales'] || {}).events || []).map((ev) => ev.date).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
        if (!dates.length) return;
        setFeed(dates);
        try { localStorage.setItem(BH_CACHE, JSON.stringify({ t: Date.now(), dates })); } catch (e) { /* ignore */ }
        onUpdate();
      })
      .catch(() => { /* offline / blocked: rules stay in charge */ });
  }

  // Debug: mbiBankHolidaysDebug(2027) → { source, dates }
  window.mbiBankHolidaysDebug = (y = new Date().getFullYear()) => ({
    source: feedYears.has(y) ? 'gov.uk' : 'rules',
    dates: Array.from(feedYears.get(y) || rulesFor(y)).sort(),
  });

  /* ---------- MBI shipping metaobject data (rev12) ----------------------- */
  // Printed by snippets/mbi-shipping-data as #MbiShippingData. Defaults =
  // the old block values, so the card still works if the JSON is missing.
  function readShipping() {
    let data = {};
    try {
      const el = document.getElementById('MbiShippingData');
      if (el) data = JSON.parse(el.textContent);
    } catch (e) { /* malformed: defaults */ }
    const nd = data.nextDay || {};
    const st = data.standard || {};
    return {
      nextDay: {
        enabled: nd.enabled !== false,
        name: nd.name || 'Next Working Day',
        price: nd.price || '',
        cutoffHour: Number.isFinite(Number(nd.cutoffHour)) ? Number(nd.cutoffHour) : 14,
      },
      standard: {
        label: st.label || 'Standard delivery',
        dispatchDays: Number(st.dispatchDays) || 0,
        transitDays: Number(st.transitDays) || 0,
      },
      closedDates: new Set(Array.isArray(data.closedDates) ? data.closedDates.filter(Boolean) : []),
      closures: Array.isArray(data.closures) ? data.closures : [],
    };
  }

  // UK calendar date (UTC-midnight Date) for a Unix-seconds instant.
  const ukDay = (sec) => { const p = ukParts(new Date(sec * 1000)); return utc(p.y, p.m, p.d); };
  const parseDay = (s) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? utc(Number(s.slice(0, 4)), Number(s.slice(5, 7)), Number(s.slice(8, 10))) : null);

  // The closure whose notice window contains today (noticeFrom … despatchResumes).
  // One at a time; first match wins.
  function activeClosure(closures, todayIso) {
    return closures.find((c) => {
      const end = c.despatchResumes;
      if (!end) return false;
      return (!c.noticeFrom || c.noticeFrom <= todayIso) && todayIso <= end;
    }) || null;
  }

  function initDelivery() {
    const renders = [];
    const ship = readShipping();
    document.querySelectorAll('[data-mbi-delivery]').forEach((box) => {
      // Warehouse closed days from the metaobject (bank holidays are automatic).
      const extraClosures = ship.closedDates;
      const isWorkingDay = (dt) => {
        const day = dt.getUTCDay();
        return day !== 0 && day !== 6 && !isBankHoliday(dt) && !extraClosures.has(iso(dt));
      };
      const addWorkingDays = (start, count) => {
        const dt = new Date(start.getTime());
        let added = 0;
        while (added < count) {
          dt.setUTCDate(dt.getUTCDate() + 1);
          if (isWorkingDay(dt)) added += 1;
        }
        return dt;
      };
      const label = (dt) => new Intl.DateTimeFormat('en-GB', {
        timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short',
      }).format(dt);

      // "midnight Thu 18 Dec" / "2pm Fri 19 Dec" (UK time). 23:59 and 00:00 both
      // read as midnight at the end of the earlier day.
      const when = (sec) => {
        const p = ukParts(new Date(sec * 1000));
        if ((p.h === 23 && p.min === 59) || (p.h === 0 && p.min === 0)) {
          const day = utc(p.y, p.m, p.d);
          if (p.h === 0) day.setUTCDate(day.getUTCDate() - 1);
          return `midnight ${label(day)}`;
        }
        const h12 = p.h % 12 || 12;
        return `${h12}${p.min ? `:${String(p.min).padStart(2, '0')}` : ''}${p.h < 12 ? 'am' : 'pm'} ${label(ukDay(sec))}`;
      };
      const later = (a, b) => (a.getTime() >= b.getTime() ? a : b);

      const closureLine = box.querySelector('[data-mbi-delivery-closure]');
      const nextLine = box.querySelector('[data-mbi-delivery-next]');
      const stdLine = box.querySelector('[data-mbi-delivery-standard]');
      const cutoff = ship.nextDay.cutoffHour;

      function render() {
        const nowSec = Date.now() / 1000;
        const now = ukParts(new Date());
        // "Today" as a UTC-midnight date for UK calendar maths.
        const today = new Date(Date.UTC(now.y, now.m - 1, now.d));
        const todayIso = iso(today);
        const beforeCutoff = isWorkingDay(today) && now.h < cutoff;
        // Order date = today if before the cut-off on a working day, else next working day.
        const orderDay = beforeCutoff ? today : addWorkingDays(today, 1);

        /* Closure (e.g. Christmas) — phases, all from the metaobject:
             before   now ≤ lastOrderDelivery            "order by … for delivery before X"
             despatch ≤ lastOrderDespatch                 "despatched before X, may arrive after"
             closed   after that, before despatchResumes  "despatched from …"
             (reopening day: no notice — a backlog message, if ever needed, goes
              in the closure's customer_note or a site banner; user 07/10)  */
        /* rev14 (07/10): event_name is a choice (Christmas, Easter, Royal Mail
           strike, Unforeseen circumstances…). PLANNED closures have last-order
           dates (phases above); DISRUPTIONS don't — they show one "notice"
           (label + customer note + "normal service from") until despatch_resumes. */
        const c = activeClosure(ship.closures, todayIso);
        const resumesDay = c ? parseDay(c.despatchResumes) : null;
        const planned = !!(c && (c.lastOrderDelivery || c.lastOrderDespatch));
        const ev = (c && c.event) || 'Christmas';
        let phase = '';
        if (c) {
          if (c.lastOrderDelivery && nowSec <= c.lastOrderDelivery) phase = 'before';
          else if (c.lastOrderDespatch && nowSec <= c.lastOrderDespatch) phase = 'despatch';
          else if (resumesDay && today < resumesDay) phase = planned ? 'closed' : 'notice';
          else phase = 'reopened';
        }
        const afterDespatchCutoff = !!(c && c.lastOrderDespatch && nowSec > c.lastOrderDespatch);

        if (closureLine) {
          const esc = (s) => String(s).replace(/[<>&]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[ch]));
          const note = c && c.note ? ` ${esc(c.note)}` : '';
          // Styled as the red notice: label regular, message bold (user mock 07/10).
          let lab = '';
          let body = '';
          if (phase === 'before') {
            lab = `${ev} delivery:`;
            body = `order by ${when(c.lastOrderDelivery)} for delivery before ${ev}.`;
          } else if (phase === 'despatch') {
            lab = `${ev} delivery:`;
            body = `orders placed by ${when(c.lastOrderDespatch)} are despatched before ${ev}, but may arrive after.`;
          } else if (phase === 'closed' && resumesDay) {
            lab = `${ev}:`;
            body = `our warehouse is closed. Orders placed now are despatched from ${label(resumesDay)}.`;
          } else if (phase === 'notice') {
            // Disruption: the note IS the message; resume date last.
            lab = `${ev}:`;
            body = c.note ? esc(c.note) : 'deliveries may take longer than usual.';
            if (resumesDay) body += ` We expect normal service from ${label(resumesDay)}.`;
          }
          const tail = phase === 'notice' ? '' : note;
          const msg = lab ? `<span class="mbi-pdp__closure-label">${esc(lab)}</span> <strong>${body}${tail}</strong>` : '';
          closureLine.innerHTML = msg;
          closureLine.hidden = !msg;
        }

        // Next-day paused window (e.g. 2pm Fri 19 Dec → Fri 2 Jan).
        const ndPaused = !!(c && c.nextDayPausedFrom && c.nextDayResumes
          && nowSec >= c.nextDayPausedFrom && nowSec < c.nextDayResumes);

        if (nextLine) {
          if (ship.nextDay.enabled && ndPaused) {
            const why = planned ? `for ${ev}` : `(${ev})`;
            nextLine.innerHTML = `${ship.nextDay.name} delivery is paused ${why}, back from <strong>${label(ukDay(c.nextDayResumes))}</strong>.`;
            nextLine.hidden = false;
          } else if (ship.nextDay.enabled) {
            const arrives = addWorkingDays(orderDay, 1);
            let lead = '';
            if (beforeCutoff) {
              const minsLeft = (cutoff * 60) - (now.h * 60 + now.min);
              const h = Math.floor(minsLeft / 60);
              const m = minsLeft % 60;
              lead = `Order within <strong>${h ? `${h}h ` : ''}${m}m</strong> for `;
            } else {
              lead = 'Order now for ';
            }
            const price = ship.nextDay.price ? ` (${ship.nextDay.price})` : '';
            nextLine.innerHTML = `${lead}delivery <strong>${label(arrives)}</strong> with ${ship.nextDay.name}${price}.`;
            nextLine.hidden = false;
          } else {
            nextLine.hidden = true;
          }
        }

        if (stdLine) {
          const dispatch = ship.standard.dispatchDays;
          const transit = ship.standard.transitDays;
          // Normal: order day + dispatch days. After the last pre-closure
          // despatch order: count from the day despatch resumes, and never
          let dispatchDay;
          if (afterDespatchCutoff && resumesDay) {
            // Closure orders are processed from the reopening day.
            dispatchDay = addWorkingDays(later(orderDay, resumesDay), dispatch);
          } else {
            dispatchDay = addWorkingDays(orderDay, dispatch);
          }
          const latest = addWorkingDays(dispatchDay, transit);
          // Before the closure deadline the warehouse guarantees delivery
          // before the event, so never show a later calculated date then.
          const promiseDay = c && c.lastOrderDespatch ? ukDay(c.lastOrderDespatch) : null;
          const est = phase === 'before' && promiseDay && latest > promiseDay
            ? `before ${c.event || 'Christmas'}`
            : label(latest);
          // Label from the metaobject; add a full stop only if it has no end punctuation.
          const stdLabel = /[.!?]$/.test(ship.standard.label) ? ship.standard.label : `${ship.standard.label}.`;
          stdLine.innerHTML = `${stdLabel} Est. <strong>${est}.</strong>`;
        }
        box.hidden = false;
      }

      render();
      renders.push(render);
      // Keep the countdown honest if the page stays open.
      window.setInterval(render, 60 * 1000);
    });
    // Refresh the GOV.UK list (weekly) and redraw if it changed anything.
    if (renders.length) refreshBankHolidays(() => renders.forEach((fn) => fn()));
  }

  /* ---------- Boost bundle: in-view red glow + pulse (rev14) ----------- */
  // While the rendered bundle sits in the middle band of the viewport it
  // gets .is-in-view (CSS warms the grey shadow to MBI Red); each time it
  // enters that band it also plays one subtle red ring pulse. Leaving the
  // band removes the glow and re-arms the pulse. Reduced motion: glow state
  // still changes (no transition), no pulse. Boost renders async, so an
  // empty wrapper keeps being observed.
  function initBundleSpotlight() {
    if (!('IntersectionObserver' in window)) return;

    const wraps = document.querySelectorAll('.mbi-pdp .boost-sd__predictive-bundle-wrapper');
    if (!wraps.length) return;

    const pulse = (wrap) => {
      if (reduceMotion) return;
      wrap.classList.remove('mbi-bundle-spotlight');
      void wrap.offsetWidth; // reflow so the animation can replay
      wrap.classList.add('mbi-bundle-spotlight');
      wrap.addEventListener('animationend', () => wrap.classList.remove('mbi-bundle-spotlight'), { once: true });
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const wrap = entry.target;
        if (!wrap.querySelector('.boost-sd__recommendation-bundle')) return;
        if (entry.isIntersecting && !wrap.classList.contains('is-in-view')) {
          wrap.classList.add('is-in-view');
          pulse(wrap);
        } else if (!entry.isIntersecting) {
          wrap.classList.remove('is-in-view');
        }
      });
    // Middle band: top 20% and bottom 35% of the screen excluded.
    }, { rootMargin: '-20% 0px -35% 0px', threshold: 0 });

    wraps.forEach((w) => {
      io.observe(w);
      // Boost may fill the wrapper while it's already on screen — IO won't
      // re-fire on its own, so re-observe once content arrives.
      const mo = new MutationObserver(() => {
        if (!w.querySelector('.boost-sd__recommendation-bundle')) return;
        mo.disconnect();
        io.unobserve(w);
        io.observe(w);
      });
      mo.observe(w, { childList: true, subtree: true });
    });
  }

  initNudges();
  initDelivery();
  initBundleSpotlight();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
